// frontend/src/App.js
import React, { useState, useEffect } from 'react';
import './styles/App.css';
import Chat from './components/Chat';
import Login from './components/Login';
import InfoModal from './components/InfoModal'; // Importa il nuovo componente InfoModal
import { createChat, getChat, API_BASE_URL } from './services/api';
import { checkAuth, logout } from './services/auth';

function App() {
  const [chatId, setChatId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false); // Stato per il modal
  
  // Controlla lo stato di autenticazione al caricamento dell'app
  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const isAuth = await checkAuth();
        setIsAuthenticated(isAuth);
      } catch (error) {
        console.error('Errore durante la verifica dell\'autenticazione:', error);
        setIsAuthenticated(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };
    
    verifyAuth();
  }, []);
  
  // Carica la chat dopo aver verificato l'autenticazione
  useEffect(() => {
    // Se stiamo ancora verificando l'autenticazione, aspetta
    if (isCheckingAuth) return;
    
    // Se non è autenticato, non caricare la chat
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    // Se l'utente è autenticato, procedi con l'inizializzazione della chat
    const initializeChat = async () => {
      try {
        console.log("Inizializzazione chat...");
        
        // Controlla se esiste già una chat salvata
        const savedChatId = localStorage.getItem('currentChatId');
        
        if (savedChatId) {
          console.log("Chat ID salvato trovato:", savedChatId);
          // Verifica che la chat esista ancora sul server
          try {
            await getChat(savedChatId);
            console.log("Chat esistente verificata con successo");
            setChatId(savedChatId);
          } catch (error) {
            console.error("Chat salvata non trovata, creazione nuova chat:", error);
            // Chat non trovata, ne crea una nuova
            const newChat = await createChat();
            console.log("Nuova chat creata:", newChat);
            setChatId(newChat.id);
            localStorage.setItem('currentChatId', newChat.id);
          }
        } else {
          console.log("Nessuna chat salvata, creazione nuova chat");
          // Crea una nuova chat
          const newChat = await createChat();
          console.log("Nuova chat creata:", newChat);
          setChatId(newChat.id);
          localStorage.setItem('currentChatId', newChat.id);
        }
      } catch (error) {
        console.error('Errore durante l\'inizializzazione della chat:', error);
        setError("Impossibile connettersi al server. Verifica la connessione di rete e riprova.");
      } finally {
        setLoading(false);
      }
    };
    
    if (isAuthenticated) {
      initializeChat();
    }
  }, [isAuthenticated, isCheckingAuth]);
  
  const startNewChat = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Creazione nuova chat...");
      const newChat = await createChat();
      console.log("Nuova chat creata:", newChat);
      setChatId(newChat.id);
      localStorage.setItem('currentChatId', newChat.id);
    } catch (error) {
      console.error('Errore durante la creazione di una nuova chat:', error);
      setError("Impossibile creare una nuova chat. Verifica la connessione di rete e riprova.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogin = (status) => {
    setIsAuthenticated(status);
  };
  
  const handleLogout = () => {
    logout();
    setIsAuthenticated(false);
  };
  
  // Funzione per aprire il modal delle informazioni
  const openInfoModal = (e) => {
    e.preventDefault();
    setIsInfoModalOpen(true);
  };
  
  // Funzione per chiudere il modal delle informazioni
  const closeInfoModal = () => {
    setIsInfoModalOpen(false);
  };
  
  // Mostra un loader mentre verifichiamo l'autenticazione
  if (isCheckingAuth) {
    return (
      <div className="login-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Caricamento...</p>
        </div>
      </div>
    );
  }
  
  // Se l'utente non è autenticato, mostra la pagina di login
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }
  
  return (
    <div className="app">
      <header className="app-header">
        <div className="app-header-content">
          <div className="app-logo">
            <img src="/images/logo.png" alt="Logo" />
          </div>
          <div>
            <h1 className="app-title">Albert</h1>
            <div className="app-subtitle">Intelligent Tutoring System</div>
          </div>
        </div>
        <div className="app-header-actions">
          <button 
            onClick={startNewChat} 
            disabled={loading}
            className="new-chat-btn"
            aria-label="Nuova chat"
          >
            <span className="new-chat-icon">+</span>
            <span>Nuova Chat</span>
          </button>
          <button 
            onClick={handleLogout}
            className="logout-btn"
            aria-label="Esci"
          >
            <span>Esci</span>
          </button>
        </div>
      </header>
      
      <main className="app-main">
        {loading ? (
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Inizializzazione della chat...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3>Errore di connessione</h3>
            <p>{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="retry-button"
            >
              Riprova
            </button>
            <div className="connection-info">
              <p><strong>Dettagli tecnici:</strong></p>
              <p>API URL: {API_BASE_URL}</p>
              <p>Se utilizzi un dispositivo mobile, assicurati che il tuo telefono e computer siano sulla stessa rete Wi-Fi</p>
            </div>
          </div>
        ) : (
          <Chat chatId={chatId} />
        )}
      </main>
      
      <footer className="app-footer">
        <p>Albert © {new Date().getFullYear()} | <a href="#about" onClick={openInfoModal}>Informazioni</a></p>
      </footer>
      
      {/* Modal delle informazioni */}
      <InfoModal isOpen={isInfoModalOpen} onClose={closeInfoModal} />
    </div>
  );
}

export default App;